import React, { useState, useEffect, useCallback } from 'react';
import axios from '../api/axios';
import { useAlert } from '../context/AlertContext';
import { io } from 'socket.io-client';
import { BACKEND_URL } from '../config';
import './RTMPMultistream.css';

const socket = io(BACKEND_URL);

const PLATFORM_PRESETS = [
  { label: 'Twitch', icon: '💜', url: 'rtmp://live.twitch.tv/app/', keyPlaceholder: 'live_xxxxxxxx' },
  { label: 'YouTube', icon: '▶️', url: 'rtmp://a.rtmp.youtube.com/live2/', keyPlaceholder: 'xxxx-xxxx-xxxx-xxxx' },
  { label: 'TikTok', icon: '🎵', url: 'rtmps://push-rtmp-f5-tt04.tiktokcdn-eu.com:443/game/', keyPlaceholder: 'Stream Key' },
  { label: 'Facebook', icon: '📘', url: 'rtmps://live-api-s.facebook.com:443/rtmp/', keyPlaceholder: 'Stream Key' },
  { label: 'Kick', icon: '🟢', url: 'rtmp://fa723fc1b171.global-contribute.live-video.net/app/', keyPlaceholder: 'Stream Key' },
  { label: 'Custom', icon: '⚙️', url: '', keyPlaceholder: 'rtmp://...' },
];

export default function RTMPMultistream() {
  const alert = useAlert();
  const [status, setStatus] = useState({ running: false, targets: [], activeForwards: [], ingestUrl: '' });
  const [showAdd, setShowAdd] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [form, setForm] = useState({ name: '', platform: 'Twitch', rtmp_url: '', stream_key: '', enabled: true });
  const [loading, setLoading] = useState(false);
  const [bitrateStats, setBitrateStats] = useState({}); // targetId -> { bitrate, fps }
  const [ingestBitrate, setIngestBitrate] = useState(null); // eingehende Bitrate
  const [titleInput, setTitleInput] = useState('');
  const [titleLoading, setTitleLoading] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const res = await axios.get('/api/rtmp/status');
      setStatus(res.data);
    } catch (e) {}
  }, []);

  useEffect(() => {
    fetchStatus();
    const interval = setInterval(fetchStatus, 3000);

    // Socket Events für Bitrate
    socket.on('rtmp:forward_stats', (data) => {
      const { targetId, bitrate, stats } = data;
      // FPS aus stats parsen
      const fpsMatch = stats?.match(/fps=\s*([\d.]+)/);
      const fps = fpsMatch ? parseFloat(fpsMatch[1]) : null;
      setBitrateStats(prev => ({
        ...prev,
        [targetId]: { bitrate: bitrate || null, fps, raw: stats }
      }));
    });

    socket.on('rtmp:stream_started', () => setIngestBitrate('verbunden'));
    socket.on('rtmp:stream_ended', () => {
      setIngestBitrate(null);
      setBitrateStats({});
    });

    // Eingehende Bitrate aus node-media-server stats
    socket.on('rtmp:ingest_stats', (data) => {
      setIngestBitrate(data.bitrate ? `${Math.round(data.bitrate)} kb/s` : null);
    });

    return () => {
      clearInterval(interval);
      socket.off('rtmp:forward_stats');
      socket.off('rtmp:stream_started');
      socket.off('rtmp:stream_ended');
      socket.off('rtmp:ingest_stats');
    };
  }, [fetchStatus]);

  const toggleServer = async () => {
    setLoading(true);
    try {
      if (status.running) {
        await axios.post('/api/rtmp/stop');
        alert.success('RTMP Server gestoppt');
      } else {
        await axios.post('/api/rtmp/start');
        alert.success('RTMP Server gestartet');
      }
      await fetchStatus();
    } catch (e) {
      alert.error('Fehler beim Umschalten des Servers');
    }
    setLoading(false);
  };

  const openAdd = () => {
    setForm({ name: 'Twitch', platform: 'Twitch', rtmp_url: 'rtmp://live.twitch.tv/app/', stream_key: '', enabled: true });
    setEditTarget(null);
    setShowAdd(true);
  };

  const openEdit = (target) => {
    // Split stored rtmp_url back into base + key
    const preset = PLATFORM_PRESETS.find(p => p.label === target.platform) || PLATFORM_PRESETS[5];
    const base = preset.url || '';
    const key = base && target.rtmp_url.startsWith(base) ? target.rtmp_url.slice(base.length) : target.rtmp_url;
    setForm({
      name: target.name,
      platform: target.platform || 'Custom',
      rtmp_url: base,
      stream_key: key,
      enabled: target.enabled === 1,
    });
    setEditTarget(target);
    setShowAdd(true);
  };

  const handlePresetChange = (label) => {
    const preset = PLATFORM_PRESETS.find(p => p.label === label);
    setForm(f => ({ ...f, platform: label, name: label, rtmp_url: preset.url, stream_key: '' }));
  };

  const saveTarget = async () => {
    const fullUrl = form.rtmp_url + form.stream_key;
    if (!form.name || !fullUrl) {
      alert.error('Name und RTMP URL / Key erforderlich');
      return;
    }
    try {
      if (editTarget) {
        await axios.put(`/api/rtmp/targets/${editTarget.id}`, {
          name: form.name,
          rtmp_url: fullUrl,
          platform: form.platform,
          enabled: form.enabled,
        });
        alert.success('Kanal aktualisiert');
      } else {
        await axios.post('/api/rtmp/targets', {
          name: form.name,
          rtmp_url: fullUrl,
          platform: form.platform,
        });
        alert.success('Kanal hinzugefügt');
      }
      setShowAdd(false);
      fetchStatus();
    } catch (e) {
      alert.error('Fehler beim Speichern');
    }
  };

  const deleteTarget = async (id) => {
    if (!window.confirm('Kanal wirklich löschen?')) return;
    await axios.delete(`/api/rtmp/targets/${id}`);
    alert.success('Kanal gelöscht');
    fetchStatus();
  };

  const toggleEnabled = async (target) => {
    await axios.put(`/api/rtmp/targets/${target.id}`, {
      name: target.name,
      rtmp_url: target.rtmp_url,
      platform: target.platform,
      enabled: target.enabled === 1 ? false : true,
    });
    fetchStatus();
  };

  const isForwarding = (id) => status.activeForwards?.includes(id);

  const updateTitle = async () => {
    if (!titleInput.trim()) return;
    setTitleLoading(true);
    try {
      const res = await axios.post('/api/stream-title/update', {
        title: titleInput.trim(),
        platforms: ['twitch']
      });
      if (res.data.updated?.length > 0) {
        alert.success(`Titel gesetzt: ${titleInput.trim()}`);
      }
      if (res.data.errors?.length > 0) {
        res.data.errors.forEach(e => alert.error(e));
      }
    } catch (e) {
      alert.error('Fehler beim Setzen des Titels');
    }
    setTitleLoading(false);
  };

  const preset = PLATFORM_PRESETS.find(p => p.label === form.platform) || PLATFORM_PRESETS[5];

  return (
    <div className="rtmp-page">
      <div className="page-header">
        <h2>📡 RTMP Multistream</h2>
        <p className="page-subtitle">Stream von OBS an mehrere Plattformen gleichzeitig weiterleiten</p>
      </div>

      {/* Ingest Info */}
      <div className="ingest-card">
        <div className="ingest-info">
          <div className="ingest-label">OBS Ingest URL</div>
          <div className="ingest-url">
            <code>{status.ingestUrl || 'rtmp://DEINE-SERVER-IP:1935/live'}</code>
            <button className="copy-btn" onClick={() => {
              navigator.clipboard.writeText(status.ingestUrl || '');
              alert.success('Kopiert!');
            }}>📋</button>
          </div>
          <div className="ingest-hint">In OBS: Einstellungen → Stream → Benutzerdefiniert → diese URL eintragen, Stream Key: <code>live</code></div>
        </div>
        <div className="server-control">
          <div className={`server-status ${status.running ? 'online' : 'offline'}`}>
            <span className="status-dot"></span>
            {status.running ? 'Server läuft' : 'Server gestoppt'}
          </div>
          {ingestBitrate && (
            <div className="ingest-bitrate">
              📥 Eingang: <strong>{ingestBitrate}</strong>
            </div>
          )}
          <button
            className={status.running ? 'btn-danger' : 'btn-success'}
            onClick={toggleServer}
            disabled={loading}
          >
            {status.running ? '⏹ Server stoppen' : '▶ Server starten'}
          </button>
        </div>
      </div>

      {/* Stream Titel */}
      <div className="title-update-card">
        <div className="title-label">🎬 Twitch Titel setzen</div>
        <div className="title-input-row">
          <input
            type="text"
            value={titleInput}
            onChange={e => setTitleInput(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && updateTitle()}
            placeholder="Stream Titel eingeben..."
            maxLength={140}
          />
          <button
            className="btn-success"
            onClick={updateTitle}
            disabled={titleLoading || !titleInput.trim()}
          >
            {titleLoading ? '...' : '✓ Setzen'}
          </button>
        </div>
      </div>

      {/* Channels */}
      <div className="channels-header">
        <h3>Kanäle</h3>
        <button className="btn-success" onClick={openAdd}>+ Kanal hinzufügen</button>
      </div>

      {status.targets?.length === 0 ? (
        <div className="empty-state">
          <p>🎯 Noch keine Kanäle eingetragen.</p>
          <p>Füge Twitch, YouTube, TikTok und mehr hinzu.</p>
        </div>
      ) : (
        <div className="channels-grid">
          {status.targets.map(target => {
            const p = PLATFORM_PRESETS.find(x => x.label === target.platform) || PLATFORM_PRESETS[5];
            const active = isForwarding(target.id);
            return (
              <div key={target.id} className={`channel-card ${target.enabled ? '' : 'disabled'} ${active ? 'forwarding' : ''}`}>
                <div className="channel-top">
                  <span className="channel-icon">{p.icon}</span>
                  <div className="channel-info">
                    <span className="channel-name">{target.name}</span>
                    <span className="channel-platform">{target.platform || 'Custom'}</span>
                  </div>
                  <div className={`forward-badge ${active ? 'live' : ''}`}>
                    {active ? '🔴 LIVE' : '⚫ Bereit'}
                  </div>
                </div>
                {active && bitrateStats[target.id] && (
                  <div className="bitrate-row">
                    {bitrateStats[target.id].bitrate && (
                      <span className="bitrate-val">📤 {Math.round(bitrateStats[target.id].bitrate)} kb/s</span>
                    )}
                    {bitrateStats[target.id].fps && (
                      <span className="bitrate-fps">{Math.round(bitrateStats[target.id].fps)} fps</span>
                    )}
                  </div>
                )}
                <div className="channel-url">
                  <code>{target.rtmp_url.replace(/\/[^/]+$/, '/***')}</code>
                </div>
                <div className="channel-actions">
                  <label className="toggle-switch" title={target.enabled ? 'Deaktivieren' : 'Aktivieren'}>
                    <input
                      type="checkbox"
                      checked={target.enabled === 1}
                      onChange={() => toggleEnabled(target)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                  <button className="btn-secondary btn-sm" onClick={() => openEdit(target)}>✏️</button>
                  <button className="btn-danger btn-sm" onClick={() => deleteTarget(target.id)}>🗑️</button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="modal rtmp-modal" onClick={e => e.stopPropagation()}>
            <h3>{editTarget ? 'Kanal bearbeiten' : 'Kanal hinzufügen'}</h3>

            <div className="form-group">
              <label>Plattform</label>
              <div className="preset-grid">
                {PLATFORM_PRESETS.map(p => (
                  <button
                    key={p.label}
                    className={`preset-btn ${form.platform === p.label ? 'active' : ''}`}
                    onClick={() => handlePresetChange(p.label)}
                    type="button"
                  >
                    <span>{p.icon}</span>
                    <span>{p.label}</span>
                  </button>
                ))}
              </div>
            </div>

            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                value={form.name}
                onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder="z.B. Twitch Main"
              />
            </div>

            {form.platform === 'Custom' ? (
              <div className="form-group">
                <label>RTMP URL (inkl. Stream Key)</label>
                <input
                  type="text"
                  value={form.stream_key}
                  onChange={e => setForm(f => ({ ...f, stream_key: e.target.value }))}
                  placeholder="rtmp://server/app/streamkey"
                />
              </div>
            ) : (
              <>
                <div className="form-group">
                  <label>RTMP Server</label>
                  <input type="text" value={form.rtmp_url} disabled className="input-disabled" />
                </div>
                <div className="form-group">
                  <label>Stream Key</label>
                  <input
                    type="password"
                    value={form.stream_key}
                    onChange={e => setForm(f => ({ ...f, stream_key: e.target.value }))}
                    placeholder={preset.keyPlaceholder}
                  />
                </div>
              </>
            )}

            {editTarget && (
              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={form.enabled}
                    onChange={e => setForm(f => ({ ...f, enabled: e.target.checked }))}
                  />
                  Aktiv (wird beim Stream automatisch weitergeleitet)
                </label>
              </div>
            )}

            <div className="modal-actions">
              <button onClick={() => setShowAdd(false)}>Abbrechen</button>
              <button className="btn-success" onClick={saveTarget}>
                {editTarget ? 'Speichern' : 'Hinzufügen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
